import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAI } from "../hooks/useAI";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { COLORS, SERIF, SANS, MONO, pageStyles } from "../components/dispute/theme";

const API_BASE = process.env.REACT_APP_API_URL || "";

const TYPE_META = {
  image: { icon: "🖼️", label: "Image" },
  // video: { icon: "📺", label: "Video" },   // video evidence disabled for now
  file: { icon: "📄", label: "File" },
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

// /dispute-evidence/:uuid — read-only table of every piece of evidence attached
// to a dispute, with each row linking to a browser view of the file/URL.
export default function DisputeEvidence() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { fetchSharedDispute } = useAI();
  const [dispute, setDispute] = useState(null);

  const load = useCallback(async () => {
    const data = await fetchSharedDispute(uuid);
    if (data.error) {
      if (data.status === 401) {
        navigate("/login");
      } else if (data.status === 403) {
        alert("You are not a party to this dispute.");
        navigate("/home");
      } else {
        alert("Dispute not found");
        navigate("/home");
      }
    } else if (data.public) {
      // Public record of a published case — the evidence files themselves stay
      // party-only, so there's nothing to show here.
      alert("Evidence files aren't part of the public record for this dispute.");
      navigate(`/dispute/${uuid}`);
    } else {
      setDispute(data);
    }
  }, [uuid, fetchSharedDispute, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (!dispute) {
    return (
      <div style={pageStyles.root}>
        <SiteHeader />
        <div style={styles.loading}>Loading evidence…</div>
      </div>
    );
  }

  // Flatten evidence across every turn, tagging each item with its turn number
  // and a browser-openable href (served file for image/file).
  // Video evidence is disabled for now — external URLs are no longer served.
  const turns = dispute.turns || [];
  const items = [];
  turns.forEach((turn, idx) => {
    (turn.evidence || []).forEach((ev) => {
      const href =
        /* ev.type === "video" ? ev.url : */ ev.fileToken
          ? `${API_BASE}/api/files/evidence/${ev.id}?token=${encodeURIComponent(ev.fileToken)}`
          : null;
      const name = ev.path ? ev.path.split("/").pop() : ev.url || "—";
      items.push({ ...ev, turnNumber: idx + 1, href, name });
    });
  });

  return (
    <div style={pageStyles.root}>
      <SiteHeader />
      <div style={pageStyles.shell}>
        <div style={pageStyles.cardWide}>
          <div style={styles.titleBlock}>
            <span style={styles.eyebrow}>Evidence</span>
            <h1 style={styles.title}>{dispute.subject || "(no subject)"}</h1>
            <div style={styles.metaLine}>
              <span>
                {items.length} evidence item{items.length === 1 ? "" : "s"} across{" "}
                {turns.length} turn{turns.length === 1 ? "" : "s"}
              </span>
            </div>
            <Link to={`/dispute/${uuid}`} style={styles.backLink}>
              ← Back to dispute
            </Link>
          </div>

          {items.length === 0 ? (
            <div style={styles.empty}>
              <h3 style={styles.emptyTitle}>No evidence submitted</h3>
              <p style={styles.emptyText}>This dispute has no attached evidence yet.</p>
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: 48 }}>#</th>
                    <th style={styles.th}>Evidence</th>
                    <th style={{ ...styles.th, width: 120 }}>Type</th>
                    <th style={{ ...styles.th, width: 72 }}>Turn</th>
                    <th style={{ ...styles.th, width: 200 }}>Added</th>
                    <th style={{ ...styles.th, width: 110 }} aria-label="Open" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((ev, i) => {
                    const meta = TYPE_META[ev.type] || TYPE_META.file;
                    return (
                      <tr key={ev.id ?? `${ev.turnNumber}-${i}`}>
                        <td style={{ ...styles.td, color: COLORS.inkMuted, fontFamily: MONO }}>
                          {i + 1}
                        </td>
                        <td style={styles.td}>
                          <div style={styles.evCell}>
                            {ev.type === "image" && ev.href ? (
                              <img src={ev.href} alt={ev.name} style={styles.thumb} />
                            ) : (
                              <span style={styles.evIcon} aria-hidden>
                                {meta.icon}
                              </span>
                            )}
                            <div style={styles.evNameCol}>
                              {ev.href ? (
                                <a
                                  href={ev.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={styles.evName}
                                  title={`Open ${meta.label.toLowerCase()}`}
                                >
                                  {ev.name}
                                </a>
                              ) : (
                                <span style={styles.evName}>{ev.name}</span>
                              )}
                              {ev.info && <div style={styles.evInfo}>{ev.info}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.typePill}>
                            {meta.icon} {meta.label}
                          </span>
                        </td>
                        <td style={{ ...styles.td, fontFamily: MONO, color: COLORS.inkSoft }}>
                          {ev.turnNumber}
                        </td>
                        <td style={{ ...styles.td, fontFamily: MONO, fontSize: 12, color: COLORS.inkMuted }}>
                          {formatDate(ev.createdAt)}
                        </td>
                        <td style={styles.td}>
                          {ev.href ? (
                            <a
                              href={ev.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.openBtn}
                            >
                              Open ↗
                            </a>
                          ) : (
                            <span style={styles.openDisabled}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const styles = {
  loading: {
    display: "flex",
    height: "60vh",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: COLORS.inkMuted,
    fontFamily: SANS,
  },
  titleBlock: { marginBottom: 24 },
  eyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 2,
    color: COLORS.inkMuted,
    fontFamily: SANS,
  },
  title: {
    fontFamily: SERIF,
    fontSize: 32,
    fontWeight: 600,
    margin: "6px 0 10px 0",
    letterSpacing: "-0.5px",
    color: COLORS.ink,
    lineHeight: 1.15,
  },
  metaLine: { fontSize: 13, color: COLORS.inkMuted, display: "flex", flexWrap: "wrap", gap: 12 },
  backLink: {
    display: "inline-block",
    marginTop: 12,
    fontSize: 12,
    color: COLORS.inkSoft,
    textDecoration: "none",
    borderBottom: `1px dashed ${COLORS.borderStrong}`,
  },

  empty: {
    textAlign: "center",
    padding: "56px 24px",
    background: COLORS.bgAlt,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
  },
  emptyTitle: { fontFamily: SERIF, fontSize: 20, margin: "0 0 8px 0", color: COLORS.ink },
  emptyText: { color: COLORS.inkMuted, margin: 0, fontSize: 13 },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
  },
  table: { width: "100%", borderCollapse: "collapse", fontFamily: SANS, fontSize: 13 },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    background: COLORS.bgAlt,
    color: COLORS.inkMuted,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    borderBottom: `1px solid ${COLORS.border}`,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 16px",
    verticalAlign: "middle",
    color: COLORS.ink,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  evCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  evNameCol: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
  evIcon: { fontSize: 20, flexShrink: 0 },
  thumb: {
    width: 36,
    height: 36,
    borderRadius: 6,
    objectFit: "cover",
    flexShrink: 0,
    border: `1px solid ${COLORS.border}`,
  },
  evName: {
    color: COLORS.ink,
    fontFamily: MONO,
    fontSize: 12,
    textDecoration: "none",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 360,
  },
  evInfo: {
    fontFamily: SANS,
    fontSize: 12,
    color: COLORS.inkSoft,
    lineHeight: 1.4,
    maxWidth: 420,
    whiteSpace: "normal",
  },
  typePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: COLORS.bgAlt,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 999,
    padding: "3px 10px",
    fontSize: 11,
    color: COLORS.inkSoft,
    whiteSpace: "nowrap",
  },
  openBtn: {
    display: "inline-block",
    padding: "6px 12px",
    background: COLORS.dark,
    color: "#fff",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  openDisabled: { color: COLORS.inkMuted },
};
